import {
  InfiniteData,
  InvalidateQueryFilters,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { PageTemplateAPI } from 'api/page-template';
import {
  PageType,
  SourceType,
  PageTemplate,
  PageTemplateLite,
  PaginatedPageTemplatesResponse,
} from 'api/page-template/types';
import { useCallback, useMemo } from 'react';

const PAGE_TEMPLATES_LIGHT_PAGE_SIZE = 20;

const getAllTemplatesKey = (pageType: PageType, sourceType: SourceType) => [
  'page-templates',
  pageType,
  sourceType,
];

const getPageTemplatesLightInfiniteKey = (
  pageType: PageType,
  sourceType: SourceType,
  limit: number,
  search?: string,
) =>
  [
    'page-templates',
    pageType,
    sourceType,
    'light-infinite',
    limit,
    search ?? '',
  ] as const;

type GetAllTemplatesQueryKeyType = ReturnType<typeof getAllTemplatesKey>;
type GetPageTemplatesLightInfiniteQueryKeyType = ReturnType<
  typeof getPageTemplatesLightInfiniteKey
>;

type GetAllPageTemplateQueryOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = Omit<
  UseQueryOptions<
    PageTemplateLite<PageTypeT, SourceTypeT>[],
    Error,
    PageTemplateLite<PageTypeT, SourceTypeT>[],
    GetAllTemplatesQueryKeyType
  >,
  'queryKey' | 'queryFn'
>;

type GetPageTemplatesLightInfiniteQueryOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = Omit<
  UseInfiniteQueryOptions<
    PaginatedPageTemplatesResponse<PageTypeT, SourceTypeT>,
    Error,
    InfiniteData<PaginatedPageTemplatesResponse<PageTypeT, SourceTypeT>>,
    PaginatedPageTemplatesResponse<PageTypeT, SourceTypeT>,
    GetPageTemplatesLightInfiniteQueryKeyType,
    number
  >,
  'queryKey' | 'queryFn' | 'initialPageParam' | 'getNextPageParam'
>;

const useGetAllPageTemplatesQuery = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageType: PageTypeT,
  sourceType: SourceTypeT,
  queryOptions?: GetAllPageTemplateQueryOptions<PageTypeT, SourceTypeT>,
): UseQueryResult<PageTemplateLite<PageTypeT, SourceTypeT>[], Error> =>
  useQuery({
    queryKey: getAllTemplatesKey(pageType, sourceType),
    queryFn: () => PageTemplateAPI.getAllPageTemplates(pageType, sourceType),
    ...queryOptions,
  });

const useGetPageTemplatesLightInfiniteQuery = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageType: PageTypeT,
  sourceType: SourceTypeT,
  params: { limit?: number; search?: string } = {},
  queryOptions?: GetPageTemplatesLightInfiniteQueryOptions<
    PageTypeT,
    SourceTypeT
  >,
): UseInfiniteQueryResult<
  InfiniteData<PaginatedPageTemplatesResponse<PageTypeT, SourceTypeT>>,
  Error
> => {
  const limit = params.limit ?? PAGE_TEMPLATES_LIGHT_PAGE_SIZE;
  const search = params.search?.trim() || undefined;

  return useInfiniteQuery({
    queryKey: getPageTemplatesLightInfiniteKey(
      pageType,
      sourceType,
      limit,
      search,
    ),
    queryFn: ({ pageParam = 0 }) =>
      PageTemplateAPI.getPageTemplatesLight(pageType, sourceType, {
        limit,
        offset: pageParam,
        search,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.hasMore) {
        return undefined;
      }

      return (allPages.length - 1) * limit + limit;
    },
    ...queryOptions,
  });
};

const useGetPageTemplatesLightWithScrollPagination = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageType: PageTypeT,
  sourceType: SourceTypeT,
  params: { limit?: number; search?: string } = {},
  queryOptions?: GetPageTemplatesLightInfiniteQueryOptions<
    PageTypeT,
    SourceTypeT
  >,
) => {
  const infiniteQuery = useGetPageTemplatesLightInfiniteQuery(
    pageType,
    sourceType,
    params,
    queryOptions,
  );

  const templates = useMemo(
    () => infiniteQuery.data?.pages?.flatMap((page) => page.data ?? []) ?? [],
    [infiniteQuery.data?.pages],
  );
  const total = infiniteQuery.data?.pages?.[0]?.total ?? 0;

  return {
    templates,
    total,
    isLoading: infiniteQuery.isLoading,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    hasNextPage: Boolean(infiniteQuery.hasNextPage),
    fetchNextPage: infiniteQuery.fetchNextPage,
    error: infiniteQuery.error,
    refetch: infiniteQuery.refetch,
  };
};

const getPageTemplateQueryKey = (pageTemplateId: number) =>
  ['page-template', pageTemplateId] as const;

type PageTemplateQueryKeyType = ReturnType<typeof getPageTemplateQueryKey>;

type GetPageTemplateQueryOptions<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = Omit<
  UseQueryOptions<
    PageTemplate<PageTypeT, SourceTypeT>,
    Error,
    PageTemplate<PageTypeT, SourceTypeT>,
    PageTemplateQueryKeyType
  >,
  'queryKey' | 'queryFn'
>;

const useGetPageTemplateQuery = <
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
>(
  pageTemplateId: number,
  queryOptions?: GetPageTemplateQueryOptions<PageTypeT, SourceTypeT>,
): UseQueryResult<PageTemplate<PageTypeT, SourceTypeT>, Error> =>
  useQuery({
    queryKey: getPageTemplateQueryKey(pageTemplateId),
    queryFn: () =>
      PageTemplateAPI.getPageTemplate<PageTypeT, SourceTypeT>(pageTemplateId),
    ...queryOptions,
  });

const useInvalidateAllPageTemplatesQuery = (
  pageType: PageType,
  sourceType: SourceType,
) => {
  const queryClient = useQueryClient();

  return useCallback(
    async (options?: InvalidateQueryFilters) => {
      await queryClient.invalidateQueries({
        queryKey: getAllTemplatesKey(pageType, sourceType),
        ...options,
      });
    },
    [queryClient, pageType, sourceType],
  );
};

const useInvalidatePageTemplateQuery = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async (pageTemplateId: number) => {
      await queryClient.invalidateQueries({
        queryKey: getPageTemplateQueryKey(pageTemplateId),
      });
    },
    [queryClient],
  );
};

interface PageTemplatePreviewParams {
  meetingId: number;
  pageType: PageType;
  enabled?: boolean;
  payload?: object;
}

const useGetPageTemplatePreviewQuery = ({
  meetingId,
  pageType,
  enabled = false,
  payload,
}: PageTemplatePreviewParams) => {
  return useQuery({
    queryKey: ['page-template-preview', meetingId, pageType],
    queryFn: ({ signal }) =>
      PageTemplateAPI.getPageLevelTemplatePreview(
        meetingId,
        pageType,
        payload,
        signal,
      ),
    enabled,
    retry: 3,
    gcTime: 0,
    staleTime: 0,
  });
};

interface SectionPreviewParams {
  meetingId: number;
  pageType: PageType;
  payload: object;
  enabled?: boolean;
  streaming?: boolean;
}
export const useGetSectionPreviewQuery = ({
  meetingId,
  pageType,
  payload,
  streaming = true,
  enabled = false,
}: SectionPreviewParams) => {
  return useQuery({
    queryKey: ['section-preview', meetingId, pageType],
    queryFn: () =>
      PageTemplateAPI.getSectionLevelPreview(
        meetingId,
        pageType,
        payload,
        streaming,
      ),
    enabled,
    retry: 3,
    gcTime: 0,
    staleTime: 0,
  });
};

export type GetAllPageTemplateQueryOption<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = GetAllPageTemplateQueryOptions<PageTypeT, SourceTypeT>;

export type GetPageTemplatesLightInfiniteQueryOption<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = GetPageTemplatesLightInfiniteQueryOptions<PageTypeT, SourceTypeT>;

export type GetPageTemplateQueryOption<
  PageTypeT extends PageType = PageType,
  SourceTypeT extends SourceType = SourceType,
> = GetPageTemplateQueryOptions<PageTypeT, SourceTypeT>;

export {
  getAllTemplatesKey,
  getPageTemplatesLightInfiniteKey,
  useGetAllPageTemplatesQuery,
  useGetPageTemplatesLightInfiniteQuery,
  useGetPageTemplatesLightWithScrollPagination,
  getPageTemplateQueryKey,
  useGetPageTemplateQuery,
  useInvalidateAllPageTemplatesQuery,
  useInvalidatePageTemplateQuery,
  useGetPageTemplatePreviewQuery,
};
